import { ExceedanceComplianceRequest, ExceedanceTimePeriod } from "src/app/models/exceedance-compliance.model";
import { getEnumLookup } from "src/app/_helpers/enum-helper";
import { SampleCategoryEnum } from "src/app/enums/sampleCategory.enum";
import { getSelectableSampleTypes } from "src/app/enums/sampleType.enum";
import { SampleStatusEnum } from "src/app/enums/sampleStatus.enum";
import nameOf from "src/app/shared/form/nameOf.helper";

export interface ExceedanceComplianceFilterValues {
    selectedAgentIds: string[];
    selectedAgentGroupIds: string[];
    selectedSurveyTypeIds: string[];
    selectedOelTypeId: string | null;
    selectedTimePeriod: ExceedanceTimePeriod | null;
    selectedJurisdictionId: string | null;
    selectedSampleCategoryId: number | null;
    selectedSampleTypeId: number | null;
    customStartDate: string | null;
    customEndDate: string | null;
}

export const EXCEEDANCE_FILTER_DEFAULTS: ExceedanceComplianceFilterValues = {
    selectedAgentIds: [],
    selectedAgentGroupIds: [],
    selectedSurveyTypeIds: [],
    selectedOelTypeId: null,
    selectedTimePeriod: ExceedanceTimePeriod.Last12Months,
    selectedJurisdictionId: null,
    selectedSampleCategoryId: null,
    selectedSampleTypeId: null,
    customStartDate: null,
    customEndDate: null,
};

export const TIME_PERIOD_OPTIONS: { id: number; name: string }[] = [
    { id: ExceedanceTimePeriod.AllDates, name: "All Dates" },
    { id: ExceedanceTimePeriod.Last12Months, name: "Last 12 Months" },
    { id: ExceedanceTimePeriod.Last6Months, name: "Last 6 Months" },
    { id: ExceedanceTimePeriod.YearToDate, name: "Year to Date" },
    { id: ExceedanceTimePeriod.Last1Year, name: "Last 1 Year" },
    { id: ExceedanceTimePeriod.Last2Years, name: "Last 2 Years" },
    { id: ExceedanceTimePeriod.Last3Years, name: "Last 3 Years" },
    { id: ExceedanceTimePeriod.Last5Years, name: "Last 5 Years" },
    { id: ExceedanceTimePeriod.Custom, name: "Custom" },
];

export interface ExceedanceWindow {
    startSeconds: number;
    endSeconds: number;
}

const ALL_DATES_FLOOR_UTC_MS = Date.UTC(2000, 0, 1);

export function resolveExceedanceWindow(period: ExceedanceTimePeriod, now: Date): ExceedanceWindow {
    const nowMs = now.getTime();
    const year = now.getUTCFullYear();
    switch (period) {
        case ExceedanceTimePeriod.Last6Months:
            return toWindow(addUtcMonths(now, -6), nowMs);
        case ExceedanceTimePeriod.YearToDate:
            return toWindow(Date.UTC(year, 0, 1), nowMs);
        case ExceedanceTimePeriod.Last1Year:
            return toWindow(Date.UTC(year - 1, 0, 1), Date.UTC(year, 0, 1));
        case ExceedanceTimePeriod.Last2Years:
            return toWindow(Date.UTC(year - 2, 0, 1), Date.UTC(year, 0, 1));
        case ExceedanceTimePeriod.Last3Years:
            return toWindow(Date.UTC(year - 3, 0, 1), Date.UTC(year, 0, 1));
        case ExceedanceTimePeriod.Last5Years:
            return toWindow(Date.UTC(year - 5, 0, 1), Date.UTC(year, 0, 1));
        case ExceedanceTimePeriod.AllDates:
            return toWindow(ALL_DATES_FLOOR_UTC_MS, nowMs);
    }
    return toWindow(addUtcMonths(now, -12), nowMs);
}

export function toDisplayRange(window: ExceedanceWindow, toDisplayDate: (seconds: number) => Date): { start: Date; end: Date } {
    const lastIncludedSeconds = Math.max(window.startSeconds, window.endSeconds - 1);
    return { start: toDisplayDate(window.startSeconds), end: toDisplayDate(lastIncludedSeconds) };
}

export function toUnixSeconds(isoDate: string): number {
    return Math.floor(Date.parse(isoDate) / 1000);
}

function toWindow(startMs: number, endMs: number): ExceedanceWindow {
    return { startSeconds: Math.floor(startMs / 1000), endSeconds: Math.floor(endMs / 1000) };
}

function addUtcMonths(date: Date, months: number): number {
    const monthIndex = date.getUTCFullYear() * 12 + date.getUTCMonth() + months;
    const year = Math.floor(monthIndex / 12);
    const month = monthIndex - year * 12;
    const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const result = new Date(date.getTime());
    result.setUTCFullYear(year, month, Math.min(date.getUTCDate(), lastDayOfMonth));
    return result.getTime();
}

export const SAMPLE_CATEGORIES = getEnumLookup(SampleCategoryEnum).filter(
    ({ id }) => id !== SampleCategoryEnum.Blank,
);

// Exclude agent-type-only classifications (Other, Vibration): they cannot have samples, so
// offering them as an exceedance/compliance sample-type filter would only ever return no results.
export const SAMPLE_TYPES = getSelectableSampleTypes();

export const SAMPLE_FILTER_KEYS = {
    jurisdictionId: 'jurisdictionId',
    sampleCategory: 'sampleCategory',
    sampleType: 'sampleType',
    sampleStatus: 'sampleStatus',
    surveyTypeId: 'sampleSurvey.surveyTypeId',
} as const;

export const APPROVED_SAMPLE_STATUSES = `${SampleStatusEnum.Approved},${SampleStatusEnum.ApprovedLetterSent}`;

export const ExceedanceSortColumn = {
    AgentName: "agentName",
    Samples: "samples",
    Exceedances: "exceedances",
    ExceedanceRate: "exceedanceRate",
    ExceedanceRateTrend: "exceedanceRateTrend",
    HighestPoel: "highestPoel",
} as const;

export type ExceedanceSortColumn = typeof ExceedanceSortColumn[keyof typeof ExceedanceSortColumn];

export const DEFAULT_EXCEEDANCE_SORT_COLUMN: ExceedanceSortColumn = ExceedanceSortColumn.ExceedanceRate;
export const DEFAULT_EXCEEDANCE_SORT_ASC = false;

export interface ExceedanceTableHeader {
    key: ExceedanceSortColumn;
    label: string;
    centered: boolean;
}

export const EXCEEDANCE_TABLE_HEADERS: ExceedanceTableHeader[] = [
    { key: ExceedanceSortColumn.AgentName, label: "Agent", centered: false },
    { key: ExceedanceSortColumn.Samples, label: "Samples", centered: true },
    { key: ExceedanceSortColumn.Exceedances, label: "Exceedances", centered: true },
    { key: ExceedanceSortColumn.ExceedanceRate, label: "Rate", centered: true },
    { key: ExceedanceSortColumn.ExceedanceRateTrend, label: "VS Prior", centered: true },
    { key: ExceedanceSortColumn.HighestPoel, label: "Highest Result", centered: true },
];

export function toNumberOrNull(value: unknown): number | null {
    if (value == null || value === "") return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
}

const SELECTED_SURVEY_TYPE_IDS_KEY = nameOf<ExceedanceComplianceFilterValues>(x => x.selectedSurveyTypeIds);
const LEGACY_SURVEY_TYPE_ID_KEY = nameOf<ExceedanceComplianceRequest>(x => x.surveyTypeId);

function toSurveyTypeIds(config: Record<string, unknown>): string[] {
    const selected = config[SELECTED_SURVEY_TYPE_IDS_KEY];
    if (Array.isArray(selected)) return selected.filter((id): id is string => typeof id === "string");
    const legacy = config[LEGACY_SURVEY_TYPE_ID_KEY];
    return typeof legacy === "string" && legacy !== "" ? [legacy] : [];
}

export function configToFilterValues(
    config: Record<string, unknown>,
): ExceedanceComplianceFilterValues {
    return {
        selectedAgentIds: Array.isArray(config["selectedAgentIds"]) ? (config["selectedAgentIds"] as string[]) : [],
        selectedAgentGroupIds: Array.isArray(config["selectedAgentGroupIds"]) ? (config["selectedAgentGroupIds"] as string[]) : [],
        selectedSurveyTypeIds: toSurveyTypeIds(config),
        selectedOelTypeId: typeof config["selectedOelTypeId"] === "string" && config["selectedOelTypeId"] !== "" ? config["selectedOelTypeId"] : null,
        selectedTimePeriod:
            (toNumberOrNull(
                config["selectedTimePeriod"],
            ) as ExceedanceTimePeriod | null) ??
            ExceedanceTimePeriod.Last12Months,
        selectedJurisdictionId: typeof config["selectedJurisdictionId"] === "string" && config["selectedJurisdictionId"] !== "" ? config["selectedJurisdictionId"] : null,
        selectedSampleCategoryId: toNumberOrNull(
            config["selectedSampleCategoryId"],
        ),
        selectedSampleTypeId: toNumberOrNull(config["selectedSampleTypeId"]),
        customStartDate: typeof config["customStartDate"] === "string" && config["customStartDate"] !== "" ? config["customStartDate"] : null,
        customEndDate: typeof config["customEndDate"] === "string" && config["customEndDate"] !== "" ? config["customEndDate"] : null,
    };
}

export function filterValuesToConfig(
    values: ExceedanceComplianceFilterValues,
): Record<string, unknown> {
    const config: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(values)) {
        if (val != null) {
            config[key] = val;
        }
    }
    return config;
}
